import { Avatar as MuiAvatar, AvatarProps, Box } from '@mui/material';
import { selectAvatarSize } from 'features/layout/features/dimensions/dimensionsSlice';
import { AvatarSize } from 'features/layout/features/dimensions/types';
import { useAppSelector } from 'store/hooks';

const scalingFactor = 0.3;
const maxSize = 120;
const minSize = 24;

const sizes: Record<AvatarSize, number> = {
  xxs: 30,
  xs: 60,
  sm: 90,
  md: 120,
  lg: 150,
  xl: 190,
};

type CustomAvatarProps = AvatarProps & {
  size?: AvatarSize;
};

const Avatar = ({ children, sx = [], size, ...props }: CustomAvatarProps) => {
  const avatarSizeStr = useAppSelector(selectAvatarSize);
  const avatarSize = sizes[size || avatarSizeStr];
  const avatarFontSize = avatarSize * scalingFactor;

  const cappedSize = Math.min(avatarSize, maxSize);

  return (
    <Box
      sx={{
        '--avatar-size': `${cappedSize}px`,
        '--avatar-font-size': `${avatarFontSize}px`,
        '@supports (width: 1cqmin)': {
          '--avatar-size': `max(${minSize}px, min(${cappedSize}px, 100cqmin))`,
          '--avatar-font-size': `min(${avatarFontSize}px, 100cqmin * ${scalingFactor})`,
        },
        height: 'var(--avatar-size)',
        width: 'var(--avatar-size)',
      }}
    >
      <MuiAvatar
        {...props}
        sx={[
          {
            width: '100%',
            height: '100%',
            flexShrink: 1,
            fontSize: 'clamp(.75rem, var(--avatar-font-size), 4rem)',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {children}
      </MuiAvatar>
    </Box>
  );
};
export default Avatar;
